import {
  confirmForgotPasswordV2,
  forgotPasswordV2,
} from "@/lib/data/requests/auth/auth";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  confirmSignIn,
  fetchAuthSession,
  setUpTOTP,
  signIn,
  updateMFAPreference,
  verifyTOTPSetup,
} from "@aws-amplify/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const getUserSessionQueryOptions = () => {
  return {
    queryKey: [queryKeys.session],
    queryFn: async () => fetchAuthSession(),
    refetchOnWindowFocus: true,
  };
};

export const useSignIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof signIn>[0]) => {
      // Keep the observed session query so sign-in can invalidate it after success.
      const isUserData = (query: { queryKey: readonly unknown[] }) =>
        query.queryKey[0] !== queryKeys.session;
      await queryClient.cancelQueries({ predicate: isUserData });
      queryClient.removeQueries({ predicate: isUserData });
      const { nextStep } = await signIn({
        ...data,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "PASSWORD_SRP", // or "PASSWORD"
        },
      });
      return nextStep;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.session] });
    },
  });
};

export const useConfirmSignIn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof confirmSignIn>[0]) => {
      const { nextStep } = await confirmSignIn({
        challengeResponse: data.challengeResponse,
      });
      return nextStep;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.session] });
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (data: Parameters<typeof forgotPasswordV2>[0]) => {
      await forgotPasswordV2(data);
    },
  });
};

export const useConfirmResetPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof confirmForgotPasswordV2>[0]) => {
      await confirmForgotPasswordV2({
        email: data.email,
        newPassword: data.newPassword,
        verificationCode: data.verificationCode,
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.session] });
    },
  });
};

export const useSetUpTOTP = () => {
  return useMutation({
    mutationFn: async () => {
      const totpSetupDetails = await setUpTOTP();
      return totpSetupDetails;
    },
  });
};

export const useVerifyTOTPSetup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { code: string }) => {
      await verifyTOTPSetup({ code: data.code });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.session] });
    },
  });
};

export const useUpdateMFAPreference = () => {
  return useMutation({
    mutationFn: async (data: Parameters<typeof updateMFAPreference>[0]) => {
      await updateMFAPreference(data);
    },
  });
};
